import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import {
    createGame, step, startWave, WIDTH, HEIGHT,
} from '../../lib/arcade/spaceInvadersEngine';
import { render as renderFrame } from '../../lib/arcade/spaceInvadersRenderer';
import { createSound } from '../../lib/arcade/spaceInvadersSound';
import { createGameLoop } from '../../lib/arcade/gameLoop';
import ArcadeLeaderboard from './leaderboard';

/**
 * Hidden Space Invaders drawer (C). Owns one engine state object, a fixed-step
 * requestAnimationFrame loop, keyboard + touch input, the HUD/screens and the
 * leaderboard. The engine and renderer are pure; everything DOM lives here.
 */
export const GAME_KEY = 'spaceInvaders';

const WAVE_BANNER_MS = 1500;
const TAP_MAX_MS = 300;
const TAP_MAX_MOVE_PX = 8;
const TOUCH_DEADZONE_PX = 2;
const HUD_RESERVED_PX = 170;
const TOUCH_CONTROLS_PX = 90;

const KEY_LEFT = ['ArrowLeft', 'KeyA'];
const KEY_RIGHT = ['ArrowRight', 'KeyD'];
const KEY_FIRE = ['Space'];
const KEY_PAUSE = ['KeyP'];
const KEY_START = ['Space', 'Enter'];

function isTouchDevice() {
    if (typeof window === 'undefined') {
        return false;
    }
    return 'ontouchstart' in window || (window.innerWidth || 0) < 768;
}

@observer
class SpaceInvaders extends Component {
    constructor(props) {
        super(props);
        // Mute choice is remembered per device (same localStorage helper as darkmode).
        const muted = !!util.get('arcade.muted');
        this.state = {
            screen: 'title',
            score: 0,
            wave: 1,
            lives: 3,
            scale: 1,
            isTouch: isTouchDevice(),
            submitting: false,
            banner: null,
            muted,
        };
        this.sound = createSound({ muted });
        this.game = null;
        this.input = { left: false, right: false, fire: false };
        this.fireOnce = false;
        this.touchTargetX = null;
        this.touchStart = null;
        this.loop = createGameLoop({ onTick: this.tick, onFrame: this.afterFrame });
        this.waveTimer = null;
        this.startedAt = 0;
        this.unmounted = false;
        this.canvasRef = null;
        this.wrapRef = null;
    }

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('resize', this.fitCanvas);
        window.addEventListener('blur', this.pause);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.fitCanvas();
        this.drawIdle();
        this.loadScores();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.scale !== this.state.scale) {
            if (this.game) {
                const ctx = this.getCtx();
                if (ctx) {
                    renderFrame(ctx, this.game);
                }
            } else {
                this.drawIdle();
            }
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stopLoop();
        this.sound.dispose();
        if (this.waveTimer) {
            clearTimeout(this.waveTimer);
            this.waveTimer = null;
        }
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('resize', this.fitCanvas);
        window.removeEventListener('blur', this.pause);
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    // --- data -------------------------------------------------------------

    loadScores = async () => {
        const { arcadeScoreStore } = this.props.stores;
        if (arcadeScoreStore?.loadLeaderboard) {
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
    }

    // --- canvas -----------------------------------------------------------

    getCtx() {
        return this.canvasRef ? this.canvasRef.getContext('2d') : null;
    }

    fitCanvas = () => {
        const wrapWidth = this.wrapRef ? this.wrapRef.clientWidth : WIDTH;
        const reserved = HUD_RESERVED_PX + (this.state.isTouch ? TOUCH_CONTROLS_PX : 0);
        const availableHeight = Math.max(HEIGHT, (window.innerHeight || HEIGHT) - reserved);
        const raw = Math.min(wrapWidth / WIDTH, availableHeight / HEIGHT);
        const scale = Math.max(1, Math.floor(raw * 2) / 2);
        if (scale !== this.state.scale) {
            this.setState({ scale });
        }
    }

    drawIdle() {
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, createGame());
        }
    }

    // --- game lifecycle ---------------------------------------------------

    startGame = () => {
        // Runs from a click / key press, which is the user gesture audio needs.
        this.sound.unlock();
        if (this.waveTimer) {
            clearTimeout(this.waveTimer);
            this.waveTimer = null;
        }
        this.game = createGame();
        this.startedAt = Date.now();
        this.input = { left: false, right: false, fire: false };
        this.fireOnce = false;
        this.touchTargetX = null;
        this.setState({
            screen: 'playing', score: 0, wave: 1, lives: 3, banner: null, submitting: false,
        }, this.startLoop);
    }

    startLoop = () => {
        if (this.unmounted) {
            return;
        }
        this.loop.start();
    }

    stopLoop = () => {
        this.loop.stop();
    }

    tick = (dtMs) => {
        if (!this.game || this.game.status !== 'playing') {
            return;
        }
        step(this.game, this.currentInput(), dtMs);
        this.fireOnce = false;
    }

    afterFrame = () => {
        if (!this.game) {
            return;
        }
        const events = this.game.events.splice(0);
        for (let i = 0; i < events.length; i += 1) {
            this.sound.play(events[i]);
        }
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, this.game);
        }
        this.syncHud();
        if (this.game.status === 'waveCleared') {
            this.stopLoop();
            this.onWaveCleared();
        } else if (this.game.status === 'gameOver') {
            this.stopLoop();
            this.onGameOver();
        }
    }

    pause = () => {
        if (this.state.screen !== 'playing') {
            return;
        }
        this.stopLoop();
        this.setState({ screen: 'paused' });
    }

    resume = () => {
        if (this.state.screen !== 'paused') {
            return;
        }
        this.sound.unlock();
        this.setState({ screen: 'playing' }, this.startLoop);
    }

    toggleMute = (e) => {
        if (e && e.currentTarget && typeof e.currentTarget.blur === 'function') {
            e.currentTarget.blur(); // keep Space firing the cannon, not this button
        }
        const muted = !this.state.muted;
        this.sound.setMuted(muted);
        util.set('arcade.muted', muted);
        this.setState({ muted });
    }

    togglePause = () => {
        if (this.state.screen === 'playing') {
            this.pause();
        } else if (this.state.screen === 'paused') {
            this.resume();
        }
    }

    onVisibility = () => {
        if (document.hidden) {
            this.pause();
        }
    }

    close = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    currentInput() {
        const input = {
            left: this.input.left,
            right: this.input.right,
            fire: this.input.fire || this.fireOnce,
        };
        if (this.touchTargetX !== null && this.game) {
            const centre = this.game.player.x + this.game.player.w / 2;
            if (this.touchTargetX < centre - TOUCH_DEADZONE_PX) {
                input.left = true;
                input.right = false;
            } else if (this.touchTargetX > centre + TOUCH_DEADZONE_PX) {
                input.right = true;
                input.left = false;
            }
        }
        return input;
    }

    syncHud() {
        const { score, wave, lives } = this.game;
        if (score !== this.state.score || wave !== this.state.wave || lives !== this.state.lives) {
            this.setState({ score, wave, lives });
        }
    }

    onWaveCleared() {
        const clearedWave = this.game.wave;
        this.setState({ banner: clearedWave });
        this.waveTimer = setTimeout(() => {
            this.waveTimer = null;
            if (this.unmounted || !this.game) {
                return;
            }
            startWave(this.game, clearedWave + 1);
            this.setState({ banner: null, wave: clearedWave + 1 }, this.startLoop);
        }, WAVE_BANNER_MS);
    }

    onGameOver = async () => {
        const { score, wave } = this.game;
        const durationMs = Date.now() - this.startedAt;
        this.setState({ screen: 'gameOver', submitting: score > 0 });
        const { arcadeScoreStore } = this.props.stores;
        if (score > 0 && arcadeScoreStore?.submitScore) {
            await arcadeScoreStore.submitScore({
                game: GAME_KEY, score, wave, durationMs,
            });
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
        if (!this.unmounted) {
            this.setState({ submitting: false });
        }
    }

    // --- keyboard ---------------------------------------------------------

    onKeyDown = (e) => {
        const { screen } = this.state;
        const { code } = e;
        if (screen === 'playing' || screen === 'paused') {
            if (KEY_LEFT.includes(code)) {
                this.input.left = true;
                e.preventDefault();
            } else if (KEY_RIGHT.includes(code)) {
                this.input.right = true;
                e.preventDefault();
            } else if (KEY_FIRE.includes(code)) {
                this.input.fire = true;
                e.preventDefault();
            } else if (KEY_PAUSE.includes(code) && !e.repeat) {
                this.togglePause();
                e.preventDefault();
            }
            return;
        }
        if ((screen === 'title' || screen === 'gameOver') && !e.repeat && KEY_START.includes(code)) {
            e.preventDefault();
            this.startGame();
        }
    }

    onKeyUp = (e) => {
        const { code } = e;
        if (KEY_LEFT.includes(code)) {
            this.input.left = false;
        }
        if (KEY_RIGHT.includes(code)) {
            this.input.right = false;
        }
        if (KEY_FIRE.includes(code)) {
            this.input.fire = false;
        }
    }

    // --- touch ------------------------------------------------------------

    canvasX(e) {
        const touch = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
        if (!touch || !this.canvasRef) {
            return null;
        }
        const rect = this.canvasRef.getBoundingClientRect();
        if (!rect.width) {
            return null;
        }
        return (touch.clientX - rect.left) / (rect.width / WIDTH);
    }

    onCanvasTouchStart = (e) => {
        if (this.state.screen !== 'playing') {
            return;
        }
        e.preventDefault();
        const x = this.canvasX(e);
        if (x === null) {
            return;
        }
        this.touchStart = { x, t: Date.now() };
        this.touchTargetX = x;
    }

    onCanvasTouchMove = (e) => {
        if (this.state.screen !== 'playing') {
            return;
        }
        e.preventDefault();
        const x = this.canvasX(e);
        if (x !== null) {
            this.touchTargetX = x;
        }
    }

    onCanvasTouchEnd = (e) => {
        if (this.state.screen !== 'playing') {
            return;
        }
        e.preventDefault();
        const x = this.canvasX(e);
        if (this.touchStart && x !== null
            && Math.abs(x - this.touchStart.x) <= TAP_MAX_MOVE_PX
            && Date.now() - this.touchStart.t <= TAP_MAX_MS) {
            this.fireOnce = true;
        }
        this.touchStart = null;
        this.touchTargetX = null;
    }

    hold = (key, value) => (e) => {
        if (e && e.cancelable) {
            e.preventDefault();
        }
        this.input[key] = value;
    }

    tapFire = (e) => {
        if (e && e.cancelable) {
            e.preventDefault();
        }
        this.fireOnce = true;
    }

    // --- render -----------------------------------------------------------

    render() {
        const {
            screen, score, wave, lives, scale, isTouch, submitting, banner, muted,
        } = this.state;
        const { arcadeScoreStore } = this.props.stores;
        const me = arcadeScoreStore?.me || null;
        const lastSubmit = arcadeScoreStore?.lastSubmit || null;
        const best = Math.max(me?.score || 0, lastSubmit?.best?.score || 0, score);
        const cssW = `${WIDTH * scale}px`;
        const cssH = `${HEIGHT * scale}px`;
        const inGame = screen === 'playing' || screen === 'paused';

        return (
            <div class='d-flex flex-column align-items-center p-3 pb-5 bg-black text-white min-vh-100' ref={c => { this.wrapRef = c; }}>
                <div class='arcade-hud d-flex justify-content-between w-100 mb-2 small' style={`max-width: ${cssW};`}>
                    <span><Text id='arcade.score'>Score</Text> {score}</span>
                    <span><Text id='arcade.wave'>Wave</Text> {wave}</span>
                    <span><Text id='arcade.lives'>Lives</Text> {'▲'.repeat(Math.max(0, lives))}</span>
                    <span><Text id='arcade.best'>Best</Text> {best}</span>
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-light py-0 px-2 lh-1'
                        onClick={this.toggleMute}
                    >
                        <i class={`fa-solid ${muted ? 'fa-volume-xmark' : 'fa-volume-high'}`} />
                        <span class='visually-hidden'>
                            {muted
                                ? <Text id='arcade.unmute'>Unmute</Text>
                                : <Text id='arcade.mute'>Mute</Text>}
                        </span>
                    </button>
                </div>

                <div class='arcade-screen' style={`width: ${cssW}; height: ${cssH};`}>
                    <canvas
                        ref={c => { this.canvasRef = c; }}
                        width={WIDTH}
                        height={HEIGHT}
                        class='arcade-canvas'
                        style={`width: ${cssW}; height: ${cssH};`}
                        onTouchStart={this.onCanvasTouchStart}
                        onTouchMove={this.onCanvasTouchMove}
                        onTouchEnd={this.onCanvasTouchEnd}
                    />

                    {screen === 'title' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.title'>Space Invaders</Text></h3>
                            <div class='small'>
                                {isTouch
                                    ? <Text id='arcade.controlsTouch'>Drag to move · tap to fire</Text>
                                    : <Text id='arcade.controlsKeyboard'>← → or A/D to move · Space to fire · P to pause</Text>}
                            </div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-success' onClick={this.startGame}>
                                    <Text id='arcade.start'>Start game</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                            <div class='small text-body-secondary'><Text id='arcade.insertCoin'>Press Space or tap Start</Text></div>
                        </div>
                    )}

                    {screen === 'paused' && (
                        <div class='arcade-overlay'>
                            <div><Text id='arcade.paused'>Paused — press P or Resume to continue</Text></div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-success btn-sm' onClick={this.resume}>
                                    <Text id='arcade.resume'>Resume</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light btn-sm' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {banner !== null && (
                        <div class='arcade-overlay'>
                            <h4 class='mb-0'>
                                <Text id='arcade.waveCleared' fields={{ wave: banner }}>Wave {banner} cleared!</Text>
                            </h4>
                        </div>
                    )}

                    {screen === 'gameOver' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.gameOver'>Game over</Text></h3>
                            <div><Text id='arcade.finalScore'>Final score</Text>: {score}</div>
                            {submitting && <div class='small'><Text id='arcade.savingScore'>Saving score…</Text></div>}
                            {!submitting && lastSubmit?.isNewBest && score > 0 && (
                                <div class='fw-bold'><Text id='arcade.newBest'>New personal best!</Text></div>
                            )}
                            <div class='d-flex gap-2'>
                                <button type='button' class='btn btn-success' onClick={this.startGame}>
                                    <Text id='arcade.playAgain'>Play again</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                                <button type='button' class='btn btn-outline-light' onClick={this.close}>
                                    <Text id='arcade.close'>Close</Text>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {isTouch && inGame && (
                    <div class='d-flex justify-content-between align-items-center w-100 mt-3' style={`max-width: ${cssW};`}>
                        <button
                            type='button'
                            class='btn btn-outline-light arcade-touch-btn'
                            onTouchStart={this.hold('left', true)}
                            onTouchEnd={this.hold('left', false)}
                            onTouchCancel={this.hold('left', false)}
                            onMouseDown={this.hold('left', true)}
                            onMouseUp={this.hold('left', false)}
                            onMouseLeave={this.hold('left', false)}
                        >
                            <i class='fa-solid fa-caret-left' />
                        </button>
                        <button
                            type='button'
                            class='btn btn-success arcade-touch-btn'
                            onTouchStart={this.tapFire}
                            onMouseDown={this.tapFire}
                        >
                            <Text id='arcade.fire'>Fire</Text>
                        </button>
                        <button
                            type='button'
                            class='btn btn-outline-light arcade-touch-btn'
                            onTouchStart={this.hold('right', true)}
                            onTouchEnd={this.hold('right', false)}
                            onTouchCancel={this.hold('right', false)}
                            onMouseDown={this.hold('right', true)}
                            onMouseUp={this.hold('right', false)}
                            onMouseLeave={this.hold('right', false)}
                        >
                            <i class='fa-solid fa-caret-right' />
                        </button>
                    </div>
                )}

                {inGame && !isTouch && (
                    <div class='d-flex gap-2 mt-3'>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.togglePause}>
                            {screen === 'paused'
                                ? <Text id='arcade.resume'>Resume</Text>
                                : <Text id='arcade.pause'>Pause</Text>}
                        </button>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.close}>
                            <Text id='arcade.close'>Close</Text>
                        </button>
                    </div>
                )}

                {(screen === 'title' || screen === 'gameOver') && (
                    <div class='w-100' style={`max-width: ${cssW};`}>
                        <ArcadeLeaderboard stores={this.props.stores} game={GAME_KEY} />
                    </div>
                )}
            </div>
        );
    }
}

export default SpaceInvaders;
